import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// region Imports - External libraries
import * as dateFns from "date-fns";
import Chart from "chart.js";
import "chartjs-plugin-zoom";
// endregion Imports - External libraries
// region Imports - Material-UI
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import ShowChartIcon from "@material-ui/icons/ShowChart";
// endregion Imports - Material-UI
// region Imports - Shared
import { CanGraphData, CanGraphField, CanGraphPoint } from "@shared/interfaces/can-graph.interface";
import { CanGraphMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Components
import Header from "@components/Header";
// endregion Imports - Components
// region Imports - Hooks
import useTranslation from "src/translations/useTranslation";
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Store
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
// endregion Imports - Store
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles
// region Imports - Local
import { FUEL_LEVEL_SMOOTHING_WINDOW, trailingMovingAverage } from "./can-graph-smoothing";
// endregion Imports - Local

interface VehicleOption {
  id_vehicle: string;
  code: string;
  description: string;
}

interface FieldDefinition {
  key: CanGraphField;
  label: CanGraphMessages;
  color: string;
}

interface RuntimeScale {
  min: number | string | Date;
  max: number | string | Date;
}

interface TimeAxisOptions {
  time?: {
    min?: Date;
    max?: Date;
    tooltipFormat?: string;
    displayFormats?: { [key: string]: string };
  };
}

type ChartWithRuntime = Chart & {
  scales?: { [key: string]: RuntimeScale | undefined };
  resetZoom?: () => void;
};

const CAN_FIELDS: FieldDefinition[] = [
  { key: "CAN_SPD", label: CanGraphMessages.fieldCanSpd, color: "#d32f2f" },
  { key: "CAN_ENGINE_RPM", label: CanGraphMessages.fieldCanEngineRpm, color: "#1976d2" },
  { key: "CAN_ENGINE_COOLANT_TEMPERATURE", label: CanGraphMessages.fieldCanEngineCoolantTemperature, color: "#f57c00" },
  { key: "CAN_FUEL_LEVEL", label: CanGraphMessages.fieldCanFuelLevelSmoothed, color: "#388e3c" },
  { key: "CAN_FUEL_CONSUMPTION", label: CanGraphMessages.fieldCanFuelConsumption, color: "#7b1fa2" },
  { key: "CAN_TOTAL_FUEL_USED", label: CanGraphMessages.fieldCanTotalFuelUsed, color: "#0097a7" },
  { key: "CAN_TOTAL_FUEL_USED_COUNTED", label: CanGraphMessages.fieldCanTotalFuelUsedCounted, color: "#00897b" },
  { key: "CAN_TOTAL_IDLE_FUEL_USED", label: CanGraphMessages.fieldCanTotalIdleFuelUsed, color: "#c2185b" },
  { key: "CAN_TOTAL_ENGINE_HOURS", label: CanGraphMessages.fieldCanTotalEngineHours, color: "#5d4037" },
  { key: "CAN_TOTAL_ENGINE_HOURS_COUNTED", label: CanGraphMessages.fieldCanTotalEngineHoursCounted, color: "#8d6e63" },
  { key: "CAN_TOTAL_DRIVING_TIME", label: CanGraphMessages.fieldCanTotalDrivingTime, color: "#455a64" },
  { key: "CAN_ODOMETER", label: CanGraphMessages.fieldCanOdometer, color: "#303f9f" },
  { key: "CAN_IGN", label: CanGraphMessages.fieldCanIgn, color: "#fbc02d" },
  { key: "CAN_GROSS_COMBINATION_VEHICLE_WEIGHT", label: CanGraphMessages.fieldCanGcvw, color: "#6a1b9a" }
];

const TRACKER_FIELDS: FieldDefinition[] = [
  { key: "SPD", label: CanGraphMessages.fieldSpd, color: "#ef6c00" },
  { key: "DIST", label: CanGraphMessages.fieldDist, color: "#1565c0" },
  { key: "H_METER", label: CanGraphMessages.fieldHMeter, color: "#2e7d32" },
  { key: "PWR_VOLT", label: CanGraphMessages.fieldPwrVolt, color: "#ad1457" }
];

const ALL_FIELDS = [...CAN_FIELDS, ...TRACKER_FIELDS];

const DEFAULT_SELECTED: CanGraphField[] = ["CAN_SPD", "CAN_ENGINE_RPM"];

interface CanGraphProps {
  /** Rendered inside the CAN telemetry tabs, which already draw the page header */
  embedded?: boolean;
}

const CanGraph: React.FC<CanGraphProps> = ({ embedded = false }) => {

  /* region Hooks */
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { screen } = useSelector((screen) => screen);
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /* endregion Hooks */

  /* region States */
  const [vehicleOptions, setVehicleOptions] = useState<VehicleOption[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>(
    dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd")
  );
  const [selectedFields, setSelectedFields] = useState<CanGraphField[]>(DEFAULT_SELECTED);
  const [graphData, setGraphData] = useState<CanGraphData | null>(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  /* endregion States */

  /* region Derived */
  const fieldLabel = useCallback((field: CanGraphField): string => {
    const definition = ALL_FIELDS.find((entry) => entry.key === field);

    return definition ? t(definition.label) : field;
  }, [t]);

  const fieldColor = useCallback((field: CanGraphField): string => {
    const definition = ALL_FIELDS.find((entry) => entry.key === field);

    return definition ? definition.color : "#000";
  }, []);
  /* endregion Derived */

  /* region Vehicle loader */
  const loadVehicles = useCallback(async () => {
    setLoadingVehicles(true);

    try {
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success" && Array.isArray(data.result)) {
        setVehicleOptions(data.result as VehicleOption[]);
      } else if (data.message) {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicles(false);
    }
  }, [addToast, t]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);
  /* endregion Vehicle loader */

  /* region Data loader */
  const handleApply = async () => {
    if (!selectedVehicle || !selectedDay) return;

    const dayStart = new Date(`${selectedDay}T00:00:00`);
    const dayEnd = new Date(`${selectedDay}T23:59:59.999`);

    setLoadingGraph(true);

    try {
      const { data } = await api.post("travels/get-can-graph-data", {
        idVehicle: selectedVehicle.id_vehicle,
        startDate: utils.convertDateToISOWithTimezone(dayStart),
        endDate: utils.convertDateToISOWithTimezone(dayEnd)
      });

      if (data.status === "success" && data.result) {
        setGraphData(data.result as CanGraphData);
      } else {
        setGraphData(null);

        if (data.message) {
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      }
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
      setGraphData(null);
    } finally {
      setLoadingGraph(false);
    }
  };
  /* endregion Data loader */

  /* region Field toggles */
  const toggleField = (field: CanGraphField) => {
    setSelectedFields((current) => (current.includes(field)
      ? current.filter((entry) => entry !== field)
      : [...current, field]));
  };

  const selectAllFields = () => setSelectedFields(ALL_FIELDS.map((entry) => entry.key));

  const clearAllFields = () => setSelectedFields([]);
  /* endregion Field toggles */

  /* region Chart lifecycle */
  /**
   * One dataset per selected field, plotted as the tracker reported it.
   *
   * The fuel level is the single exception: the float swings with the truck and the raw line is full
   * of steps that look like a drain but are not one, so it is drawn as the moving average of the
   * last readings — and its legend entry says so, because a smoothed line must never pass for a
   * measurement.
   */
  const buildDatasets = useCallback((points: CanGraphPoint[]): Chart.ChartDataSets[] => (
    selectedFields.map((field) => {
      const readings = points.map((point) => point[field] ?? null);
      const series = field === "CAN_FUEL_LEVEL"
        ? trailingMovingAverage(readings, FUEL_LEVEL_SMOOTHING_WINDOW)
        : readings;

      return {
        label: fieldLabel(field),
        data: points.map((point, index) => ({
          x: new Date(point.occurrenceDate),
          y: series[index]
        })) as unknown as number[],
        borderColor: fieldColor(field),
        backgroundColor: fieldColor(field),
        borderWidth: 1.5,
        pointRadius: 1.5,
        pointHoverRadius: 4,
        fill: false,
        spanGaps: true,
        lineTension: 0
      };
    })
  ), [selectedFields, fieldLabel, fieldColor]);

  useEffect(() => {
    if (!canvasRef.current || !graphData) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      return;
    }

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const datasets = buildDatasets(graphData.points);

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        legend: { display: true, position: "bottom" },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: (items) => {
              const first = items[0];

              if (!first) return "";

              const point = (first as unknown as { xLabel: Date }).xLabel;

              return point instanceof Date ? dateFns.format(point, "dd/MM/yyyy HH:mm:ss") : String(point);
            }
          }
        },
        hover: { mode: "index", intersect: false },
        scales: {
          xAxes: [{
            type: "time",
            time: {
              tooltipFormat: "DD/MM/YYYY HH:mm:ss",
              displayFormats: {
                millisecond: "HH:mm:ss.SSS",
                second: "HH:mm:ss",
                minute: "HH:mm",
                hour: "HH:mm"
              }
            },
            ticks: { autoSkip: true, maxRotation: 0 }
          }],
          yAxes: [{
            ticks: { beginAtZero: false }
          }]
        },
        plugins: {
          zoom: {
            pan: { enabled: true, mode: "x" },
            zoom: { enabled: true, mode: "x", drag: false, speed: 0.1 }
          }
        }
      }
    });

    // Tear-down handled by next effect run or unmount cleanup below.
    // eslint-disable-next-line consistent-return
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [graphData, buildDatasets]);
  /* endregion Chart lifecycle */

  /* region Zoom controls */
  const applyZoom = (factor: number) => {
    const chart = chartRef.current as ChartWithRuntime | null;

    if (!chart || !graphData || graphData.points.length === 0) return;

    const xScale = chart.scales?.["x-axis-0"];

    if (!xScale) return;

    const currentMin = xScale.min instanceof Date ? xScale.min.getTime() : Number(xScale.min);
    const currentMax = xScale.max instanceof Date ? xScale.max.getTime() : Number(xScale.max);

    if (!Number.isFinite(currentMin) || !Number.isFinite(currentMax)) return;

    const center = (currentMin + currentMax) / 2;
    const halfRange = ((currentMax - currentMin) / 2) * factor;
    const xAxisOptions = chart.options.scales?.xAxes?.[0] as TimeAxisOptions | undefined;

    if (!xAxisOptions) return;

    if (!xAxisOptions.time) xAxisOptions.time = {};

    xAxisOptions.time.min = new Date(center - halfRange);
    xAxisOptions.time.max = new Date(center + halfRange);
    chart.update();
  };

  const handleZoomIn = () => applyZoom(0.5);

  const handleZoomOut = () => applyZoom(2);

  const handleResetZoom = () => {
    const chart = chartRef.current as ChartWithRuntime | null;

    if (!chart) return;

    const xAxisOptions = chart.options.scales?.xAxes?.[0] as TimeAxisOptions | undefined;

    if (xAxisOptions?.time) {
      delete xAxisOptions.time.min;
      delete xAxisOptions.time.max;
    }

    if (typeof chart.resetZoom === "function") {
      chart.resetZoom();
    } else {
      chart.update();
    }
  };
  /* endregion Zoom controls */

  /* region Render helpers */
  const renderFieldRow = (definition: FieldDefinition) => {
    const isChecked = selectedFields.includes(definition.key);

    return (
      <FormControlLabel
        key={definition.key}
        className="field-row"
        control={(
          <Checkbox
            size="small"
            checked={isChecked}
            onChange={() => toggleField(definition.key)}
          />
        )}
        label={(
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="color-dot" style={{ backgroundColor: definition.color }} />
            <span>{t(definition.label)}</span>
          </span>
        )}
      />
    );
  };

  const sourceChip = useMemo(() => {
    if (!graphData) return null;

    const labelKey = graphData.source === "cache" ? CanGraphMessages.sourceCache : CanGraphMessages.sourceData;
    const tooltipKey = graphData.source === "cache"
      ? CanGraphMessages.sourceTooltipCache
      : CanGraphMessages.sourceTooltipData;

    return (
      <Tooltip title={t(tooltipKey)}>
        <Chip
          className={`source-chip ${graphData.source}`}
          label={`${t(CanGraphMessages.sourceLabel)}: ${t(labelKey)}`}
          size="small"
        />
      </Tooltip>
    );
  }, [graphData, t]);
  /* endregion Render helpers */

  const hasData = !!graphData && graphData.points.length > 0;

  return (
    <>
      {!embedded && <Header title={t(CanGraphMessages.title)} />}
      <Container platform={screen.platform as ScreenPlatform} embedded={embedded} className="page">
        <div className="toolbar">
          <Autocomplete
            className="vehicle-input"
            size="small"
            options={vehicleOptions}
            value={selectedVehicle}
            loading={loadingVehicles}
            onChange={(_event, value) => setSelectedVehicle(value)}
            getOptionLabel={(option) => `${option.code} - ${option.description}`}
            getOptionSelected={(option, value) => option.id_vehicle === value.id_vehicle}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(CanGraphMessages.vehicle)}
                variant="outlined"
                size="small"
              />
            )}
          />
          <TextField
            className="day-input"
            label={t(CanGraphMessages.day)}
            type="date"
            size="small"
            variant="outlined"
            value={selectedDay}
            onChange={(event) => setSelectedDay(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: dateFns.format(new Date(), "yyyy-MM-dd")
            }}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedVehicle || !selectedDay || loadingGraph}
            onClick={handleApply}
          >
            {loadingGraph ? <CircularProgress size={20} color="inherit" /> : t(CanGraphMessages.apply)}
          </Button>
          {sourceChip}
          {graphData && (
            <>
              <div className="info-block">
                <span>{t(CanGraphMessages.devId)}</span>
                <strong>{graphData.devId}</strong>
              </div>
              <div className="info-block">
                <span>{t(CanGraphMessages.pointsCount)}</span>
                <strong>{graphData.points.length}</strong>
              </div>
            </>
          )}
        </div>

        <div className="body">
          <aside className="sidebar">
            <div className="actions">
              <Button size="small" variant="outlined" onClick={selectAllFields}>
                {t(CanGraphMessages.selectAll)}
              </Button>
              <Button size="small" variant="outlined" onClick={clearAllFields}>
                {t(CanGraphMessages.clearAll)}
              </Button>
            </div>
            <div className="field-group">
              <span className="group-title">{t(CanGraphMessages.fieldsCanGroup)}</span>
              {CAN_FIELDS.map(renderFieldRow)}
            </div>
            <div className="field-group">
              <span className="group-title">{t(CanGraphMessages.fieldsTrackerGroup)}</span>
              {TRACKER_FIELDS.map(renderFieldRow)}
            </div>
          </aside>

          <section className="chart-area">
            <div className="zoom-controls">
              <Tooltip title={t(CanGraphMessages.zoomIn)}>
                <span>
                  <IconButton size="small" onClick={handleZoomIn} disabled={!hasData}>
                    <ZoomInIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t(CanGraphMessages.zoomOut)}>
                <span>
                  <IconButton size="small" onClick={handleZoomOut} disabled={!hasData}>
                    <ZoomOutIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t(CanGraphMessages.resetZoom)}>
                <span>
                  <IconButton size="small" onClick={handleResetZoom} disabled={!hasData}>
                    <ZoomOutMapIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>

            {hasData ? (
              <div className="chart-wrapper">
                <canvas ref={canvasRef} />
              </div>
            ) : (
              <div className="empty-state">
                <ShowChartIcon className="icon" />
                <Typography variant="h6">
                  {loadingGraph ? t(CanGraphMessages.loading) : t(CanGraphMessages.noDataTitle)}
                </Typography>
                <Typography variant="body2">
                  {t(CanGraphMessages.noDataDescription)}
                </Typography>
              </div>
            )}
          </section>
        </div>
      </Container>
    </>
  );
};

export default CanGraph;
